/**
 * Ask-AI assistants, launch URLs and per-product research prompts.
 *
 * Prefill is only enabled where a current, first-party URL format is reliable:
 * - ChatGPT: `https://chatgpt.com/?q=`
 * - Perplexity: `https://www.perplexity.ai/search?q=`
 *
 * Clipboard fallback for Grok, Claude and Gemini.
 */

export type AskAiProviderId =
  | 'chatgpt'
  | 'perplexity'
  | 'grok'
  | 'claude'
  | 'gemini';

export interface AskAiProvider {
  id: AskAiProviderId;
  name: string;
  homeUrl: string;
  supportsPrefill: boolean;
  tooltip: string;
  ariaLabel: string;
}

const PROVIDER_BASE: Omit<AskAiProvider, 'tooltip' | 'ariaLabel'>[] = [
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    homeUrl: 'https://chatgpt.com/',
    supportsPrefill: true,
  },
  {
    id: 'perplexity',
    name: 'Perplexity',
    homeUrl: 'https://www.perplexity.ai/',
    supportsPrefill: true,
  },
  {
    id: 'grok',
    name: 'Grok',
    homeUrl: 'https://grok.com/',
    supportsPrefill: false,
  },
  {
    id: 'claude',
    name: 'Claude',
    homeUrl: 'https://claude.ai/new',
    supportsPrefill: false,
  },
  {
    id: 'gemini',
    name: 'Gemini',
    homeUrl: 'https://gemini.google.com/app',
    supportsPrefill: false,
  },
];

export function buildAskAiProviders(productName: string): AskAiProvider[] {
  return PROVIDER_BASE.map((provider) => ({
    ...provider,
    tooltip: provider.supportsPrefill
      ? `Open ${provider.name} with a ${productName} research prompt`
      : `Copy the prompt and open ${provider.name}`,
    ariaLabel: provider.supportsPrefill
      ? `Ask ${provider.name} about ${productName}`
      : `Ask ${provider.name} about ${productName}. Prompt will be copied so you can paste it.`,
  }));
}

export function askAiLaunchUrl(
  provider: AskAiProvider,
  prompt: string,
): string {
  if (!provider.supportsPrefill) return provider.homeUrl;
  const q = encodeURIComponent(prompt);
  if (provider.id === 'chatgpt') return `https://chatgpt.com/?q=${q}`;
  if (provider.id === 'perplexity') {
    return `https://www.perplexity.ai/search?q=${q}`;
  }
  return provider.homeUrl;
}

export function buildAskAiPrompt({
  productName,
  siteUrl,
  askAiUrl,
  extraFocus,
}: {
  productName: string;
  siteUrl: string;
  askAiUrl?: string;
  extraFocus?: string;
}): string {
  const sources = askAiUrl
    ? `${siteUrl} and ${askAiUrl}`
    : siteUrl;
  const focus = extraFocus ? ` ${extraFocus}` : '';
  return `Explain what ${productName} does, who it is designed for, its main features and current pricing. Compare it with relevant alternatives and help me decide whether it suits my property photography or property marketing workflow. Stay specific to ${productName} rather than describing the wider ReHub Studio suite, except to note a shared login or credits when that is published. Use current information from ${sources} and link to the most relevant ${productName} pages.${focus}`;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }

  try {
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.left = '-9999px';
    field.style.top = '0';
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(field);
    return ok;
  } catch {
    return false;
  }
}
